import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * `Ф0` (кабинет ПЗС): одно состояние обновления на весь кабинет.
 *
 * Шапка показывает «обновлено ЧЧ:ММ:СС»; страницы сообщают, когда данные
 * обновились (`markUpdated`) или фоновое обновление сорвалось (`markFailed`).
 * При сбое экран **не пропадает**: остаются последние данные, индикатор меняет
 * цвет, а `refresh()` даёт страницам сигнал повторить загрузку.
 */

export type CabinetRefreshValue = {
  updatedAt: Date | null;
  failed: boolean;
  /** Растёт на каждый ручной повтор — страницы перезапрашивают данные. */
  version: number;
  markUpdated: (at?: Date) => void;
  markFailed: () => void;
  refresh: () => void;
};

const CabinetRefreshContext = createContext<CabinetRefreshValue | null>(null);

export function CabinetRefreshProvider({
  children,
  initialUpdatedAt = new Date(),
}: {
  children: React.ReactNode;
  initialUpdatedAt?: Date | null;
}) {
  const [updatedAt, setUpdatedAt] = useState<Date | null>(initialUpdatedAt);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);

  const markUpdated = useCallback((at: Date = new Date()) => {
    setUpdatedAt(at);
    setFailed(false);
  }, []);

  const markFailed = useCallback(() => {
    setFailed(true);
  }, []);

  const refresh = useCallback(() => {
    setFailed(false);
    setVersion((current) => current + 1);
  }, []);

  const value = useMemo(
    () => ({ updatedAt, failed, version, markUpdated, markFailed, refresh }),
    [updatedAt, failed, version, markUpdated, markFailed, refresh],
  );

  return (
    <CabinetRefreshContext.Provider value={value}>
      {children}
    </CabinetRefreshContext.Provider>
  );
}

export function useCabinetRefresh(): CabinetRefreshValue {
  const value = useContext(CabinetRefreshContext);

  if (!value) {
    throw new Error("кабинет: useCabinetRefresh вне CabinetRefreshProvider");
  }

  return value;
}
